import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, Send, BellOff, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Email preferences card on the Profile page.
 * For now just controls the Weekly Recap (Resend) — toggle off + "send me one now" test button.
 */
export default function WeeklyRecapSettings() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const optedOut = !!user?.email_opt_out_recap;

  const toggle = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/email/weekly-recap/prefs`, { opt_out: !optedOut });
      toast.success(!optedOut ? "You'll no longer receive the Weekly Recap." : "Weekly Recap re-enabled.");
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send the Weekly Recap to your email right now? (Useful to preview the layout.)")) return;
    setSending(true);
    try {
      await axios.post(`${API}/email/weekly-recap/send`);
      toast.success("📬 Sent — check your inbox in a minute.", { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      data-testid="weekly-recap-settings"
      className="mt-8 p-5 border border-zinc-800 bg-[#0F0F0F]"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
        Email Preferences
      </div>
      <h3 className="font-display text-2xl text-white mb-3 flex items-center gap-2">
        <Mail size={18} className="text-[#D4AF37]" />
        Weekly Recap
      </h3>
      <p className="text-sm text-neutral-400 mb-4 leading-relaxed">
        Every Monday at 00:00 PT, we'll email you a recap of your previous week —
        Phoenix Tokens earned, badges unlocked, streaks, and your top hype partner. Drives the
        rhythm so you keep flying higher. 🐦‍🔥
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          data-testid="recap-toggle-btn"
          onClick={toggle}
          disabled={busy}
          className={`btn-omni ${optedOut ? "btn-red" : "btn-outline-gold"} disabled:opacity-60`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : optedOut ? <Bell size={14} /> : <BellOff size={14} />}
          {optedOut ? "Re-enable Weekly Recap" : "Opt out of Weekly Recap"}
        </button>
        <button
          data-testid="recap-send-now-btn"
          onClick={sendNow}
          disabled={sending}
          className="btn-omni btn-outline-gold disabled:opacity-60"
        >
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Send Me a Test Recap
        </button>
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-widest text-neutral-600">
        Email goes to <span className="text-neutral-400">{user?.email || "—"}</span>
      </p>
    </section>
  );
}
